import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { can, USER_TYPE_LABELS } from '../lib/permissions';
import { canViewAgreements } from '../utils/agreements';
import { LayoutDashboard, Calendar, BarChart3, Users, LogOut, Building2, Inbox, BookOpen, FileText, CalendarDays } from 'lucide-react';
import Logo from './Logo';
import MessagingHub from './MessagingHub';
import ProfileModal from './ProfileModal';

const STATIC_NAV_ITEMS = [
  { to: '/panel', label: 'Inicio', icon: LayoutDashboard, end: true },
  { to: '/panel/calendario', label: 'Calendario', icon: Calendar },
  { to: '/panel/documentos', label: 'Documentos', icon: FileText },
  { to: '/panel/informes', label: 'Informes', icon: BarChart3, permission: 'viewReports' },
  { to: '/panel/equipo', label: 'Equipo', icon: Users, permission: 'viewEquipo' },
  { to: '/panel/aprobaciones', label: 'Aprobaciones', icon: Inbox, permission: 'approveEvents' },
  // Regla solo-web (ver src/utils/agreements.js): admins, supervisor e
  // inspector ven convenios; editar depende del rol y de la empresa.
  { to: '/panel/convenios', label: 'Convenios', icon: BookOpen, allow: canViewAgreements },
  { to: '/panel/festivos', label: 'Festivos', icon: CalendarDays, permission: 'manageHolidays' },
];

// El item de empresas es dinámico: list global para inspector/super,
// "Mi empresa" para admin empresa y "Mi sede" para admin sede.
const buildEmpresasItem = (user) => {
  const t = user?.type;
  if (t === 4 || t === 5) {
    return { to: '/panel/empresas', label: 'Empresas', icon: Building2 };
  }
  if (t === 1 && user?.companyId != null) {
    return { to: `/panel/empresas/${user.companyId}`, label: 'Mi empresa', icon: Building2 };
  }
  if (t === 2 && user?.companyId != null) {
    return { to: `/panel/empresas/${user.companyId}`, label: 'Mi sede', icon: Building2 };
  }
  return null;
};

const PanelLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const userType = user?.type ?? 0;
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const visibleItems = (() => {
    const base = STATIC_NAV_ITEMS.filter(
      (item) => (item.allow
        ? item.allow(userType)
        : (!item.permission || can(userType, item.permission)))
    );
    const empresas = buildEmpresasItem(user);
    if (!empresas) return base;
    // Insertamos justo antes de "Convenios" para mantener el orden visual.
    const conveniosIdx = base.findIndex((i) => i.to === '/panel/convenios');
    const insertAt = conveniosIdx === -1 ? base.length : conveniosIdx;
    return [...base.slice(0, insertAt), empresas, ...base.slice(insertAt)];
  })();

  const roleLabel = USER_TYPE_LABELS[userType] || 'Trabajador';

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1680px] mx-auto px-6 md:px-10 xl:px-14 h-16 flex items-center gap-6">
          <NavLink to="/panel" className="flex items-center gap-2.5 shrink-0">
            <Logo size={32} variant="onLight" />
            <span className="font-display font-bold text-lg text-ink hidden sm:inline">TemControl</span>
          </NavLink>

          <nav className="flex-1 flex justify-center">
            <ul className="flex items-center gap-1 overflow-x-auto">
              {visibleItems.map(({ to, label, icon: Icon, end }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${
                        isActive
                          ? 'bg-primary-soft text-primary-darker'
                          : 'text-ink-muted hover:text-ink hover:bg-slate-100'
                      }`
                    }
                  >
                    <Icon size={16} />
                    <span className="hidden md:inline">{label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={() => setProfileOpen(true)}
              className="flex items-center gap-3 hover:bg-slate-100 rounded-lg pl-2 pr-1 py-1 transition-colors"
              title="Ver mi perfil"
            >
              <div className="text-right hidden lg:block">
                <div className="text-sm font-semibold text-ink leading-tight">{user?.nombre || 'Usuario'}</div>
                <div className="text-xs text-ink-muted leading-tight">{roleLabel}{user?.source === 'mock' ? ' · Demo' : ''}</div>
              </div>
              <div className="w-9 h-9 rounded-full bg-primary-soft text-primary-darker flex items-center justify-center font-bold text-sm ring-2 ring-transparent hover:ring-primary transition">
                {(user?.nombre || 'U').charAt(0).toUpperCase()}
              </div>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-ink-muted hover:text-danger px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors text-sm font-medium"
              title="Salir"
            >
              <LogOut size={16} /> <span className="hidden lg:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1680px] mx-auto px-6 md:px-10 xl:px-14 py-8">
        <Outlet />
      </main>

      {/* FAB de mensajería visible en todas las páginas autenticadas. */}
      <MessagingHub />

      <ProfileModal
        open={profileOpen}
        user={user}
        onClose={() => setProfileOpen(false)}
      />
    </div>
  );
};

export default PanelLayout;
